import type { GameCardProps } from "@/components/layout/GameCard";
import type { TournamentListItem } from "@/services/tournament.service";
import type { TournamentRegistrationStatus } from "@/services/tournament-registration.service";
import { getChargedAmount } from "@/utils/payment.utils";
import {
  canOpenTournamentRegistration,
  shouldNavigateToTournamentDetail,
} from "@/utils/tournament.utils";
import { formatBrDateTime } from "@/utils/br-datetime";

const COVER_PLACEHOLDER =
  "https://images.unsplash.com/photo-1517927033932-b3d18e61fb3a?q=80&w=1600&auto=format&fit=crop";

function moneyToNumber(value: number | string | undefined | null): number {
  if (value == null) return 0;
  const n = typeof value === "string" ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : 0;
}

export {
  canOpenTournamentRegistration,
  getTournamentExplorePath,
  getTournamentRegistrationPath,
  shouldNavigateToTournamentDetail,
} from "@/utils/tournament.utils";

function formatStartsAt(iso: string | null): string {
  if (!iso) return "Data a definir";
  try {
    return formatBrDateTime(iso);
  } catch {
    return "Data a definir";
  }
}

function tournamentVenueLabel(t: TournamentListItem): string {
  const name = t.venueName?.trim();
  const addr = t.venueAddress?.trim();
  if (name && addr) return `${name} — ${addr}`;
  return name || addr || "Local a divulgar";
}

function getPresentation(t: TournamentListItem): Pick<GameCardProps, "status" | "statusLabel" | "statusVariant" | "meta"> {
  const now = new Date();
  const maxSlots =
    t.registrationType === "TEAM" ? t.maxTeamRegistrations : t.maxIndividualRegistrations;
  const current = t.registrationsCount;
  const isFull = maxSlots != null && maxSlots > 0 && current >= maxSlots;

  const ongoing = t.status !== "DRAFT" && t.status !== "COMPLETED";

  const closeAt = t.registrationCloseAt ? new Date(t.registrationCloseAt) : null;
  const openAt = t.registrationOpenAt ? new Date(t.registrationOpenAt) : null;
  const regClosedByDate = closeAt != null && closeAt < now;
  const regNotYetOpen = openAt != null && openAt > now;

  const meta =
    t.category?.trim() ||
    (t.classifyFormat === "GROUPS" ? "Fase de grupos" : "Todos contra todos");

  if (ongoing) {
    return {
      status: "interest-open",
      statusLabel: "Em andamento",
      statusVariant: "violet",
      meta,
    };
  }
  if (isFull) {
    return {
      status: "full",
      statusLabel: "Lotado",
      statusVariant: "neutral",
      meta,
    };
  }
  if (regNotYetOpen) {
    return {
      status: "just-added",
      statusLabel: "Inscrições em breve",
      statusVariant: "info",
      meta,
    };
  }
  if (regClosedByDate) {
    return {
      status: "interest-open",
      statusLabel: "Inscrições encerradas",
      statusVariant: "neutral",
      meta,
    };
  }
  return {
    status: "just-added",
    statusLabel: "Inscrições abertas",
    statusVariant: "success",
    meta,
  };
}

export type TournamentCardRegistrationContext = {
  isRegistered: boolean;
  userRegistrationStatus?: TournamentRegistrationStatus | null;
  paymentExpiresAt?: string | null;
};

export function tournamentToCardProps(
  t: TournamentListItem,
  onAction?: () => void,
  registration?: TournamentCardRegistrationContext,
): GameCardProps {
  const pres = getPresentation(t);
  const maxSlots =
    t.registrationType === "TEAM" ? t.maxTeamRegistrations : t.maxIndividualRegistrations;
  /** Sem limite no cadastro: mostrar só o total de inscritos (texto), sem barra — não esconder o bloco. */
  const unlimitedCap = maxSlots == null || maxSlots <= 0;
  const hideProgress = false;
  const maxPlayers = unlimitedCap ? 0 : maxSlots;

  const basePrice =
    t.registrationType === "TEAM"
      ? moneyToNumber(t.teamRegistrationPrice)
      : moneyToNumber(t.individualRegistrationPrice);
  const price = getChargedAmount(basePrice, "PIX", t.passFeesToUser ?? true);

  const image =
    t.coverImage?.trim() ||
    t.organizerTeam?.photo?.trim() ||
    COVER_PLACEHOLDER;

  const organizer = t.organizerTeam;

  const now = new Date();
  const userStatus = registration?.userRegistrationStatus ?? null;
  const goToDetail = shouldNavigateToTournamentDetail(t, userStatus);
  const canRegisterNow = canOpenTournamentRegistration(t, now);

  const baseActionVariant = pres.status === "full" ? "outline" : "primary";
  let actionLabel = goToDetail
    ? "Ver torneio"
    : canRegisterNow
      ? "Inscrever-se"
      : "Ver inscrição";
  let actionVariant: GameCardProps["actionVariant"] = goToDetail
    ? "secondary"
    : baseActionVariant;
  let actionIcon: string | undefined =
    goToDetail || !canRegisterNow ? undefined : "emoji_events";

  let finalStatus = pres.status;
  let finalStatusLabel = pres.statusLabel;
  let finalStatusVariant = pres.statusVariant;
  let timeLeft: string | undefined;
  let countdown: GameCardProps["countdown"];

  const isRegistered = registration?.isRegistered ?? false;

  if (isRegistered) {
    if (userStatus === "PENDING_PAYMENT") {
      finalStatus = "payment-due";
      finalStatusLabel = "Pagamento Pendente";
      finalStatusVariant = "warning";

      const paymentExpiresAt = registration?.paymentExpiresAt;
      if (paymentExpiresAt) {
        const expiresAt = new Date(paymentExpiresAt);
        if (expiresAt > now) {
          const diff = expiresAt.getTime() - now.getTime();
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          timeLeft =
            hours > 0 ? `${hours}h ${minutes}m restantes` : `${minutes}m restantes`;
          countdown = {
            days: 0,
            hours,
            minutes,
            seconds: Math.floor((diff % (1000 * 60)) / 1000),
          };
        }
      }
    } else if (userStatus === "CONFIRMED") {
      finalStatus = "just-added";
      finalStatusLabel = "Inscrito";
      finalStatusVariant = "success";
    }

    actionLabel = userStatus === "CONFIRMED" ? "Ver torneio" : "Continuar";
    actionVariant = "primary";
    actionIcon = undefined;
  }

  return {
    id: t.id,
    title: t.name,
    image,
    date: formatStartsAt(t.startsAt),
    location: tournamentVenueLabel(t),
    sport: t.sport,
    currentPlayers: t.registrationsCount ?? 0,
    maxPlayers,
    hideProgress,
    price,
    actionLabel,
    actionVariant,
    actionIcon,
    teamName: organizer?.name,
    teamId: organizer?.id,
    teamSlug: organizer?.slug,
    teamPhoto: organizer?.photo ?? undefined,
    entityLabel: "Torneio",
    entityIcon: "emoji_events",
    entityBadgeClassName:
      "bg-gradient-to-r from-amber-500 to-orange-500 text-white ring-1 ring-amber-200/60 shadow-sm shadow-amber-500/30",
    progressUnitLabel: t.registrationType === "TEAM" ? "Equipes" : "Inscrições",
    onAction,
    status: finalStatus,
    statusLabel: finalStatusLabel,
    statusVariant: finalStatusVariant,
    meta: pres.meta,
    timeLeft,
    countdown,
  };
}
